import { useState, useEffect } from 'react'
import emailjs from '@emailjs/browser'
import { useTranslation } from '../hooks/useTranslation'
import MailIcon from '../assets/icons/mail.svg'
import LocationIcon from '../assets/icons/location.svg'
import ClockIcon from '../assets/icons/clock.svg'
const contactBg = '/images/contact-hero.png'
import WorldMap from '../components/WorldMap'

// 世界地图上的标记点坐标（百分比）- 只包含坐标，国家名称从翻译数据获取
// priority: true 表示重点地区（需要置顶和加粗）
// 注意：数组顺序必须与 locales.json 中的 countries 顺序保持一致，以确保索引匹配
const markerCoordinates = {
  northAmerica: [
    { key: 'united_states', x: 15, y: 38, priority: true },
    { key: 'canada', x: 18, y: 22, priority: true }
  ],
  europe: [
    { key: 'germany', x: 53, y: 28, priority: true },
    { key: 'france', x: 50, y: 32, priority: false },
    { key: 'united_kingdom', x: 48, y: 26, priority: true },
    { key: 'spain', x: 47, y: 36, priority: false },
    { key: 'italy', x: 54, y: 34, priority: false },
    { key: 'netherlands', x: 51, y: 27, priority: false },
    { key: 'belgium', x: 50, y: 28, priority: false },
    { key: 'switzerland', x: 52, y: 31, priority: false },
    { key: 'sweden', x: 55, y: 18, priority: false },
    { key: 'poland', x: 56, y: 28, priority: false }
  ],
  asia: [
    // 顺序必须与 locales.json 中的 countries.asia 保持一致
    { key: 'china', x: 68, y: 38, priority: true },
    { key: 'hong_kong', x: 76, y: 44, priority: true },
    { key: 'macao', x: 75, y: 45, priority: true },
    { key: 'japan', x: 82, y: 33, priority: true },
    { key: 'south_korea', x: 77, y: 34, priority: true },
    { key: 'singapore', x: 74, y: 52, priority: false },
    { key: 'uae', x: 59, y: 40, priority: false },
    { key: 'saudi_arabia', x: 58, y: 42, priority: false },
    { key: 'vietnam', x: 75, y: 50, priority: true },
    { key: 'cambodia', x: 76, y: 52, priority: true },
    { key: 'mongolia', x: 72, y: 30, priority: true }
  ],
  oceania: [
    { key: 'australia', x: 82, y: 72, priority: false },
    { key: 'new_zealand', x: 90, y: 80, priority: false }
  ],
  africa: [
    // 顺序必须与 locales.json 中的 countries.africa 保持一致
    { key: 'senegal', x: 44, y: 48, priority: true },
    { key: 'cape_verde', x: 41, y: 50, priority: true },
    { key: 'egypt', x: 58, y: 36, priority: false }
  ]
}

// 国家键名映射（用于从翻译数据中获取）
const countryKeys = {
  northAmerica: ['northAmerica'],
  europe: ['europe'],
  asia: ['asia'],
  oceania: ['oceania'],
  africa: ['africa']
}

const continentNames = {
  northAmerica: { en: 'North America', zh: '北美洲' },
  europe: { en: 'Europe', zh: '欧洲' },
  asia: { en: 'Asia', zh: '亚洲' },
  oceania: { en: 'Oceania', zh: '大洋洲' },
  africa: { en: 'Africa', zh: '非洲' }
}

export default function Contact() {
  const { t, getPageTitle, lang } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  })
  const [formStatus, setFormStatus] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hoveredMarker, setHoveredMarker] = useState(null)
  const [activeContinent, setActiveContinent] = useState('all')

  // 更新页面标题
  useEffect(() => {
    document.title = getPageTitle('contact')
  }, [lang, getPageTitle])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setFormStatus(null)

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      company: formData.company,
      subject: formData.subject,
      message: formData.message,
      to_email: 'Prime@ccicm.co'
    }

    try {
      await sendEmail(templateParams)
      setFormStatus('success')
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' })
    } catch (error) {
      console.error('邮件发送失败:', error)
      setFormStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const sendEmail = async (params) => {
    // 本地开发环境使用 Vite 代理
    const localEmailApi = import.meta.env.VITE_LOCAL_EMAIL_API
    if (localEmailApi) {
      const response = await fetch(localEmailApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new Error('邮件服务不可用')
      }

      return await response.json()
    }

    // 生产环境使用 Cloudflare Worker
    const workerUrl = window.CLOUDFLARE_WORKER_URL
    if (workerUrl) {
      const response = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new Error('邮件服务不可用')
      }

      return await response.json()
    }

    const emailJsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY

    if (emailJsPublicKey) {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID',
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID',
        params,
        emailJsPublicKey
      )
      return { success: true }
    }

    // 备用方案：使用 mailto 链接
    const subjectLine = lang === 'en'
      ? `Inquiry from ${params.from_name}`
      : `来自 ${params.from_name} 的咨询`

    const mailtoLink = `mailto:Prime@ccicm.co?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(
      `${lang === 'en' ? 'Name' : '姓名'}: ${params.from_name}\n${lang === 'en' ? 'Email' : '邮箱'}: ${params.from_email}\n${lang === 'en' ? 'Phone' : '电话'}: ${params.phone}\n${lang === 'en' ? 'Company' : '公司'}: ${params.company}\n\n${params.message}`
    )}`

    window.open(mailtoLink, '_blank')
    return { success: true }
  }

  const getCountryName = (continent, index) => {
    const regionsData = t('contact.details.regions.countries')
    if (regionsData && regionsData[continent]) {
      return regionsData[continent][index] || 'Unknown'
    }
    return 'Unknown'
  }

  const getContinentName = (key) => {
    return t(`contact.details.regions.${key}`) || key
  }

  const isRegionCountry = (continent, index) => {
    // 亚洲的香港和澳门是地区（索引 1 和 2）
    return continent === 'asia' && (index === 1 || index === 2)
  }

  // 获取某个大洲的国家数量
  const getCountryCount = (continent) => {
    const regionsData = t('contact.details.regions.countries')
    if (regionsData && regionsData[continent]) {
      return regionsData[continent].length
    }
    return 0
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero hero-with-bg">
        <div className="hero-bg-image">
          <img src={contactBg} alt="Office handshake" />
          <div className="hero-overlay"></div>
        </div>
        <div className="container hero-content">
          <h1>{t('contact.hero.title')}</h1>
          <p>{t('contact.hero.subtitle')}</p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2>{t('contact.info.title')}</h2>
              <p>{t('contact.info.description')}</p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <img src={MailIcon} alt="Email" />
                  </div>
                  <div className="contact-item-text">
                    <h4>{t('contact.details.email.label')}</h4>
                    <p><a href="mailto:Prime@ccicm.co">{t('contact.details.email.value')}</a></p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <img src={LocationIcon} alt="Location" />
                  </div>
                  <div className="contact-item-text">
                    <h4>{t('contact.details.address.label')}</h4>
                    <p>{t('contact.details.address.value')}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <img src={ClockIcon} alt="Clock" />
                  </div>
                  <div className="contact-item-text">
                    <h4>{t('contact.details.hours.label')}</h4>
                    <p>{t('contact.details.hours.value')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h3>{t('contact.form.title')}</h3>
              <p>{t('contact.form.subtitle')}</p>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contact.form.labels.name')} *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.name')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">{t('contact.form.labels.email')} *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.email')}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">{t('contact.form.labels.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.phone')}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">{t('contact.form.labels.company')}</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.company')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('contact.form.labels.subject')}</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.form.placeholders.subject')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.form.labels.message')} *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.placeholders.message')}
                    required
                  />
                </div>

                <div className="form-submit">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                  </button>
                </div>

                {formStatus === 'success' && (
                  <div className="form-message success">
                    {t('contact.form.success')}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="form-message error">
                    {t('contact.form.error')}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Global Coverage Map Section */}
      <section className="section global-coverage-section">
        <div className="container">
          <div className="section-header">
            <h2>{t('contact.details.regions.label')}</h2>
            <p>{t('contact.details.regions.value')}</p>
          </div>

          {/* Continent Filter Tabs */}
          <div className="continent-filter">
            <button
              className={`filter-btn ${activeContinent === 'all' ? 'active' : ''}`}
              onClick={() => setActiveContinent('all')}
            >
              {lang === 'zh' || lang === 'zh-HK' ? '全部' : 'All'}
            </button>
            <button
              className={`filter-btn ${activeContinent === 'northAmerica' ? 'active' : ''}`}
              onClick={() => setActiveContinent('northAmerica')}
            >
              {getContinentName('northAmerica')}
            </button>
            <button
              className={`filter-btn ${activeContinent === 'europe' ? 'active' : ''}`}
              onClick={() => setActiveContinent('europe')}
            >
              {getContinentName('europe')}
            </button>
            <button
              className={`filter-btn ${activeContinent === 'asia' ? 'active' : ''}`}
              onClick={() => setActiveContinent('asia')}
            >
              {getContinentName('asia')}
            </button>
            <button
              className={`filter-btn ${activeContinent === 'oceania' ? 'active' : ''}`}
              onClick={() => setActiveContinent('oceania')}
            >
              {getContinentName('oceania')}
            </button>
            <button
              className={`filter-btn ${activeContinent === 'africa' ? 'active' : ''}`}
              onClick={() => setActiveContinent('africa')}
            >
              {getContinentName('africa')}
            </button>
          </div>

          {/* Interactive World Map */}
          <div className="world-map-container">
            <div className="world-map">
              <WorldMap
                activeContinent={activeContinent}
                hoveredMarker={hoveredMarker}
                setHoveredMarker={setHoveredMarker}
                markerCoordinates={markerCoordinates}
                getCountryName={getCountryName}
              />
            </div>

            {/* 地图图例 - 左下角 */}
            <div className="map-legend map-legend-bottom-left">
              <div className="legend-item">
                <span className="legend-dot"></span>
                <span>{lang === 'zh' || lang === 'zh-HK' ? '服务覆盖区域' : 'Service Coverage Area'}</span>
              </div>
            </div>
          </div>

          {/* 地区详细列表 */}
          <div className="regions-grid">
            {Object.keys(markerCoordinates).map((continent) => {
              const countryCount = getCountryCount(continent)
              const countriesData = t('contact.details.regions.countries')
              const countriesList = countriesData?.[continent] || []
              const continentMarkers = markerCoordinates[continent] || []

              // 创建国家索引映射，用于查找 priority
              const countryPriorityMap = {}
              continentMarkers.forEach(marker => {
                countryPriorityMap[marker.key] = marker.priority || false
              })

              // 排序：重点地区置顶
              const sortedCountries = [...countriesList].map((name, index) => {
                const key = continentMarkers[index]?.key
                return { name, index, priority: countryPriorityMap[key] || false }
              }).sort((a, b) => {
                if (a.priority && !b.priority) return -1
                if (!a.priority && b.priority) return 1
                return 0
              })

              return (
                <div
                  key={continent}
                  className={`region-card ${activeContinent !== 'all' && activeContinent !== continent ? 'region-card-dimmed' : ''}`}
                >
                  <h3>
                    <span className="continent-icon">{getContinentName(continent)}</span>
                    <span className="country-count">
                      {continent === 'asia'
                        ? `(${countryCount} ${t('contact.details.regions.asiaCountriesCount')})`
                        : `(${countryCount} ${t('contact.details.regions.countriesCount')})`
                      }
                    </span>
                  </h3>
                  <ul className="country-list">
                    {sortedCountries.map((item) => (
                      <li key={item.index} className={`country-item ${activeContinent !== 'all' && activeContinent !== continent ? 'country-item-dimmed' : ''} ${item.priority ? 'country-item-priority' : ''}`}>
                        <span className="country-dot"></span>
                        <span className="country-name">
                          {item.name}
                          {continent === 'asia' && isRegionCountry(continent, item.index) && (
                            <span className="region-tag">{t('contact.details.regions.regionLabel') || (lang === 'zh' || lang === 'zh-HK' ? '地区' : 'Region')}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}
